import React, { HTMLInputTypeAttribute, ReactNode } from 'react'
import { SizeVariants } from '@/shared/forms/components/constants'
import clsx from 'clsx'
import OptionalLabel from '@/shared/forms/components/OptionalLabel'
import { SimpleTooltip } from '@/shared/components/common/Tooltip'
import { useFormContext } from 'react-hook-form'
import DropdownSelect from '@/shared/components/DropdownSelect'

type Option = {
  id: string
  label: string
}

type Props = {
  name: string
  label: string
  options: Option[]
  size?: keyof typeof SizeVariants
  disabled?: boolean
  type?: HTMLInputTypeAttribute
  optional?: boolean
  tooltip?: ReactNode
  autoComplete?: string

  labelWhenNotSet?: string
}

export default function RHFDropdown({
  name,
  label,
  options,
  disabled,
  labelWhenNotSet,
  tooltip,
  autoComplete,
  size = 'full',
  optional = false,
}: Props) {
  const { register, formState, trigger } = useFormContext()
  const { isSubmitting, touchedFields, dirtyFields, errors } = formState
  const isDisabled = disabled || isSubmitting
  const field = register(name)
  const isTouched = touchedFields?.[name] || dirtyFields?.[name]
  const hasError = !!errors[name]

  return (
    <div className={SizeVariants[size as keyof typeof SizeVariants]}>
      {label && (
        <div className="flex items-end justify-between">
          <div className="flex items-center gap-x-2">
            <label
              htmlFor={field.name}
              className={clsx(
                'block text-sm font-medium leading-6',
                disabled ? 'text-gray-500' : 'text-gray-900',
              )}
            >
              {label}
            </label>
            {tooltip && <SimpleTooltip content={tooltip} />}
          </div>
          <OptionalLabel optional={optional} />
        </div>
      )}
      <DropdownSelect
        name={name}
        options={options}
        onChange={async (e) => {
          await field.onChange(e)
          await trigger(name)
        }}
        touched={isTouched}
        error={hasError}
        disabled={isDisabled}
        autoComplete={autoComplete}
        emptyLabel={labelWhenNotSet}
        field={field}
        classes="w-full"
      />
      {isTouched && hasError && (
        <div className="mt-2 text-sm font-semibold text-red-600">
          {`${errors[name]!.message}`}
        </div>
      )}
    </div>
  )
}
